import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Collapse, Container, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton,
  InputAdornment, MenuItem, Paper, TextField, Tooltip, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { useNotifier } from "../../../Core/Notifier";
import { api, T, inputSx, fmtCAD } from "./api";
import { uploadImage } from "../../../Components/UploadFile/upload";
import { API_URL } from "../../../config";

const buildImageUrl = (path: string | null | undefined): string | null => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${API_URL}/../${path}`;
};

/* ═══════════════════════════════════════════════════════════════════════════
   CATEGORY FORM MODAL
   ═══════════════════════════════════════════════════════════════════════════ */
const CategoryModal: React.FC<{
  open: boolean; category: any | null; serviceId: number;
  onSave: (d: any) => void; onClose: () => void; saving: boolean;
}> = ({ open, category, serviceId, onSave, onClose, saving }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (category) { setName(category.name || ""); setDescription(category.description || ""); }
    else { setName(""); setDescription(""); }
  }, [category, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "rgba(59,130,246,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CategoryOutlinedIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.white }}>
            {category?.id ? "Edit Category" : "New Category"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
        <TextField label="Category Name *" size="small" value={name} onChange={(e) => setName(e.target.value)} autoFocus fullWidth sx={inputSx} />
        <TextField label="Description" size="small" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} sx={inputSx} />
      </DialogContent>
      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave({ id: category?.id, service_id: serviceId, name: name.trim(), description: description || null })}
          disabled={saving || !name.trim()}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}>
          {saving ? "Saving..." : "Save Category"}
        </Button>
      </Box>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   SUBCATEGORY FORM MODAL
   ═══════════════════════════════════════════════════════════════════════════ */
const SubcategoryModal: React.FC<{
  open: boolean; subcategory: any | null; serviceId: number; categoryId: number;
  categoryName: string; onSave: (d: any) => void; onClose: () => void; saving: boolean;
}> = ({ open, subcategory, serviceId, categoryId, categoryName, onSave, onClose, saving }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (subcategory) { setName(subcategory.name || ""); setDescription(subcategory.description || ""); }
    else { setName(""); setDescription(""); }
  }, [subcategory, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "rgba(139,92,246,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FolderOpenOutlinedIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.white }}>
            {subcategory?.id ? "Edit Subcategory" : "New Subcategory"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
        <TextField label="Parent Category" size="small" value={categoryName} disabled fullWidth sx={inputSx} />
        <TextField label="Subcategory Name *" size="small" value={name} onChange={(e) => setName(e.target.value)} autoFocus fullWidth sx={inputSx} />
        <TextField label="Description" size="small" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} sx={inputSx} />
      </DialogContent>
      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Cancel</Button>
        <Button variant="contained"
          onClick={() => onSave({ id: subcategory?.id, service_id: serviceId, category_id: categoryId, name: name.trim(), description: description || null })}
          disabled={saving || !name.trim()}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}>
          {saving ? "Saving..." : "Save Subcategory"}
        </Button>
      </Box>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   ITEM FORM MODAL
   ═══════════════════════════════════════════════════════════════════════════ */
const ItemModal: React.FC<{
  open: boolean; item: any | null; serviceId: number; categoryId: number;
  subcategoryId: number | null; parentLabel: string;
  onSave: (d: any) => void; onClose: () => void; saving: boolean;
}> = ({ open, item, serviceId, categoryId, subcategoryId, parentLabel, onSave, onClose, saving }) => {
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("");
  const [defaultPrice, setDefaultPrice] = useState("");
  const [sourceType, setSourceType] = useState("inhouse");
  const [status, setStatus] = useState("active");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (item) {
      setName(item.name || ""); setUnit(item.unit || "");
      setDefaultPrice(item.default_price?.toString() || "");
      setSourceType(item.source_type || "inhouse");
      setStatus(item.status || "active"); setDescription(item.description || "");
      setImageUrl(item.image_url || null);
    } else {
      setName(""); setUnit(""); setDefaultPrice(""); setSourceType("inhouse"); setStatus("active"); setDescription(""); setImageUrl(null);
    }
  }, [item, open]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/") || file.size > 2 * 1024 * 1024) return;
    setUploading(true);
    try { const path = await uploadImage(file); if (path) setImageUrl(path); } catch {}
    setUploading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "rgba(45,106,53,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <InventoryOutlinedIcon sx={{ fontSize: 16, color: T.greenTx }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.white }}>{item?.id ? "Edit Item" : "New Item"}</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField label="Parent" size="small" value={parentLabel} disabled fullWidth sx={inputSx} />
        <TextField label="Item Name *" size="small" value={name} onChange={(e) => setName(e.target.value)} autoFocus fullWidth sx={inputSx} />
        {/* Image Upload */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {imageUrl ? (
            <Box sx={{ position: "relative", width: 80, height: 60, borderRadius: "10px", overflow: "hidden", border: `1px solid ${T.line}` }}>
              <Box component="img" src={buildImageUrl(imageUrl) || ""} alt="Item" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <IconButton size="small" onClick={() => setImageUrl(null)} sx={{ position: "absolute", top: 1, right: 1, bgcolor: "rgba(0,0,0,0.5)", width: 18, height: 18, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}>
                <CloseIcon sx={{ fontSize: 12, color: "#fff" }} />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ width: 80, height: 60, borderRadius: "10px", border: `1.5px dashed ${T.line}`, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: T.bg }}>
              <ImageOutlinedIcon sx={{ fontSize: 22, color: T.slateLt }} />
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Button component="label" size="small" variant="outlined" disabled={uploading}
              startIcon={uploading ? <CircularProgress size={12} /> : <CloudUploadOutlinedIcon sx={{ fontSize: 14 }} />}
              sx={{ textTransform: "none", fontSize: 11, fontWeight: 600, borderColor: T.line, color: T.slate, borderRadius: "8px" }}>
              {uploading ? "Uploading..." : imageUrl ? "Change Image" : "Upload Image"}
              <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
            </Button>
            <Typography sx={{ fontSize: 10, color: T.slateLt, mt: 0.3 }}>JPG, PNG, WebP. Max 2MB.</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField label="Unit" size="small" placeholder="e.g. per_plate" value={unit} onChange={(e) => setUnit(e.target.value)} sx={inputSx} />
          <TextField label="Default Price" size="small" type="number" value={defaultPrice} onChange={(e) => setDefaultPrice(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={inputSx} />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField select label="Source Type" size="small" value={sourceType} onChange={(e) => setSourceType(e.target.value)} sx={inputSx}>
            <MenuItem value="inhouse">In-House</MenuItem>
            <MenuItem value="vendor">Vendor</MenuItem>
            <MenuItem value="both">Both</MenuItem>
          </TextField>
          <TextField select label="Status" size="small" value={status} onChange={(e) => setStatus(e.target.value)} sx={inputSx}>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Box>
        <TextField label="Description" size="small" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} sx={inputSx} />
      </DialogContent>
      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Cancel</Button>
        <Button variant="contained" disabled={saving || !name.trim()}
          onClick={() => onSave({
            id: item?.id, service_id: serviceId, category_id: categoryId,
            subcategory_id: subcategoryId, name: name.trim(), unit: unit || null,
            default_price: defaultPrice ? Number(defaultPrice) : null,
            source_type: sourceType, status, description: description || null, image_url: imageUrl || null,
          })}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}>
          {saving ? "Saving..." : "Save Item"}
        </Button>
      </Box>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   DELETE CONFIRM DIALOG
   ═══════════════════════════════════════════════════════════════════════════ */
const DeleteDialog: React.FC<{
  target: { id: number; name: string; type: string } | null;
  deleting: boolean; onConfirm: () => void; onClose: () => void;
}> = ({ target, deleting, onConfirm, onClose }) => (
  <Dialog open={!!target} onClose={onClose} maxWidth="xs" PaperProps={{ sx: { borderRadius: "14px" } }}>
    <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 16 }}>
      <WarningAmberIcon color="error" /> Delete {target?.type}?
    </DialogTitle>
    <DialogContent>
      <DialogContentText sx={{ fontSize: 13 }}>
        Delete <strong>{target?.name}</strong>
        {target?.type === "category" ? " and all its subcategories and items?" :
          target?.type === "subcategory" ? " and all its items?" : "?"}
      </DialogContentText>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose} sx={{ textTransform: "none", color: T.slate }}>Cancel</Button>
      <Button variant="contained" color="error" onClick={onConfirm} disabled={deleting}
        sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}>{deleting ? "..." : "Delete"}</Button>
    </DialogActions>
  </Dialog>
);

/* ═══════════════════════════════════════════════════════════════════════════
   ITEM ROW
   ═══════════════════════════════════════════════════════════════════════════ */
const ItemRow: React.FC<{
  item: any; onEdit: (i: any) => void; onDelete: (i: any) => void;
}> = ({ item, onEdit, onDelete }) => (
  <Box sx={{
    display: "flex", alignItems: "center", justifyContent: "space-between",
    py: 1, px: 2, ml: 1, borderRadius: "8px",
    border: `1px solid ${T.line}`, bgcolor: T.white,
    transition: "all 0.15s", "&:hover": { bgcolor: "#fef6ee", borderColor: T.gold },
  }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
      {item.image_url ? (
        <Box component="img" src={buildImageUrl(item.image_url) || ""} alt=""
          sx={{ width: 32, height: 32, borderRadius: "6px", objectFit: "cover", border: `1px solid ${T.line}`, flexShrink: 0 }} />
      ) : (
        <Box sx={{ width: 32, height: 32, borderRadius: "6px", bgcolor: T.greenBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <InventoryOutlinedIcon sx={{ fontSize: 14, color: T.greenTx }} />
        </Box>
      )}
      <Box>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: T.navy }}>{item.name}</Typography>
        {item.unit && <Typography sx={{ fontSize: 10, color: T.slateLt }}>{item.unit}</Typography>}
      </Box>
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {item.default_price != null && (
        <Chip label={fmtCAD(Number(item.default_price))} size="small"
          sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: T.greenBg, color: T.greenTx }} />
      )}
      <Chip label={item.status ?? "active"} size="small" sx={{
        height: 20, fontSize: 10, fontWeight: 600,
        bgcolor: (item.status ?? "active") === "active" ? T.greenBg : T.redBg,
        color: (item.status ?? "active") === "active" ? T.greenTx : T.redTx,
      }} />
      <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(item)} sx={{ color: T.slateLt, "&:hover": { color: T.gold } }}>
        <EditOutlinedIcon sx={{ fontSize: 14 }} />
      </IconButton></Tooltip>
      <Tooltip title="Delete"><IconButton size="small" onClick={() => onDelete(item)} sx={{ color: T.slateLt, "&:hover": { color: T.redTx } }}>
        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
      </IconButton></Tooltip>
    </Box>
  </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════
   SUBCATEGORY CARD (collapsible)
   ═══════════════════════════════════════════════════════════════════════════ */
const SubcategoryCard: React.FC<{
  sub: any; serviceId: number; categoryId: number;
  onEditSub: (s: any) => void; onDeleteSub: (s: any) => void;
  onAddItem: (catId: number, subId: number, parentLabel: string) => void;
  onEditItem: (i: any) => void; onDeleteItem: (i: any) => void;
}> = ({ sub, serviceId, categoryId, onEditSub, onDeleteSub, onAddItem, onEditItem, onDeleteItem }) => {
  const [open, setOpen] = useState(false);
  const items = sub.items ?? [];

  return (
    <Box sx={{ ml: 1 }}>
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        py: 1.2, px: 2, borderRadius: "10px", bgcolor: "#f9f7fd",
        border: `1px solid #e8e4f0`, cursor: "pointer",
        transition: "all 0.15s", "&:hover": { bgcolor: "#f3f0fa" },
      }} onClick={() => setOpen(!open)}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton size="small" sx={{ p: 0, color: "#8b5cf6" }}>
            {open ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
          </IconButton>
          <FolderOpenOutlinedIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{sub.name}</Typography>
          <Chip label={`${items.length} item${items.length !== 1 ? "s" : ""}`} size="small"
            sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: T.greenBg, color: T.greenTx }} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Add Item">
            <IconButton size="small" onClick={() => onAddItem(categoryId, sub.id, sub.name)}
              sx={{ color: T.greenTx, "&:hover": { bgcolor: T.greenBg } }}>
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit"><IconButton size="small" onClick={() => onEditSub(sub)} sx={{ color: T.slateLt, "&:hover": { color: T.gold } }}>
            <EditOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton></Tooltip>
          <Tooltip title="Delete"><IconButton size="small" onClick={() => onDeleteSub(sub)} sx={{ color: T.slateLt, "&:hover": { color: T.redTx } }}>
            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          </IconButton></Tooltip>
        </Box>
      </Box>

      <Collapse in={open} timeout={250}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, pt: 0.8, pl: 2 }}>
          {items.length === 0 ? (
            <Typography sx={{ fontSize: 11, color: T.slateLt, py: 1, pl: 2, fontStyle: "italic" }}>
              No items yet
            </Typography>
          ) : items.map((item: any) => (
            <ItemRow key={item.id} item={item} onEdit={onEditItem} onDelete={onDeleteItem} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   CATEGORY CARD (collapsible) — contains subcategories + direct items
   ═══════════════════════════════════════════════════════════════════════════ */
const CategoryCard: React.FC<{
  cat: any; serviceId: number; serviceColor: string;
  onEditCat: (c: any) => void; onDeleteCat: (c: any) => void;
  onAddSub: (catId: number, catName: string) => void;
  onEditSub: (s: any) => void; onDeleteSub: (s: any) => void;
  onAddItem: (catId: number, subId: number | null, parentLabel: string) => void;
  onEditItem: (i: any) => void; onDeleteItem: (i: any) => void;
}> = ({ cat, serviceId, serviceColor, onEditCat, onDeleteCat, onAddSub, onEditSub, onDeleteSub, onAddItem, onEditItem, onDeleteItem }) => {
  const [open, setOpen] = useState(false);
  const subs = cat.subcategories ?? [];
  const directItems = cat.items ?? [];
  const subItemCount = subs.reduce((t: number, s: any) => t + (s.items ?? []).length, 0);
  const totalItems = directItems.length + subItemCount;

  return (
    <Paper elevation={0} sx={{ borderRadius: "14px", border: `1px solid ${T.line}`, overflow: "hidden", transition: "all 0.15s" }}>
      {/* Category header */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        py: 1.5, px: 2.5, cursor: "pointer",
        bgcolor: open ? "#f8f6f2" : T.white,
        transition: "background 0.15s", "&:hover": { bgcolor: "#f8f6f2" },
      }} onClick={() => setOpen(!open)}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <IconButton size="small" sx={{ p: 0, color: "#3b82f6" }}>
            {open ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
          </IconButton>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: serviceColor, flexShrink: 0 }} />
          <CategoryOutlinedIcon sx={{ fontSize: 18, color: "#3b82f6" }} />
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy }}>{cat.name}</Typography>
          <Chip label={`${subs.length} sub`} size="small"
            sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: "#8b5cf612", color: "#8b5cf6" }} />
          <Chip label={`${totalItems} item${totalItems !== 1 ? "s" : ""}`} size="small"
            sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: T.greenBg, color: T.greenTx }} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Add Subcategory">
            <IconButton size="small" onClick={() => onAddSub(cat.id, cat.name)}
              sx={{ color: "#8b5cf6", "&:hover": { bgcolor: "#8b5cf612" } }}>
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Item (direct)">
            <IconButton size="small" onClick={() => onAddItem(cat.id, null, cat.name)}
              sx={{ color: T.greenTx, "&:hover": { bgcolor: T.greenBg } }}>
              <InventoryOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit"><IconButton size="small" onClick={() => onEditCat(cat)} sx={{ color: T.slateLt, "&:hover": { color: T.gold } }}>
            <EditOutlinedIcon sx={{ fontSize: 15 }} />
          </IconButton></Tooltip>
          <Tooltip title="Delete"><IconButton size="small" onClick={() => onDeleteCat(cat)} sx={{ color: T.slateLt, "&:hover": { color: T.redTx } }}>
            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
          </IconButton></Tooltip>
        </Box>
      </Box>

      {/* Expanded content */}
      <Collapse in={open} timeout={300}>
        <Box sx={{ px: 2.5, pb: 2, pt: 0.5 }}>
          {cat.description && (
            <Typography sx={{ fontSize: 11.5, color: T.slateLt, mb: 1.5, pl: 1 }}>{cat.description}</Typography>
          )}

          {/* Subcategories */}
          {subs.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: directItems.length > 0 ? 1.5 : 0 }}>
              {subs.map((sub: any) => (
                <SubcategoryCard key={sub.id} sub={sub} serviceId={serviceId} categoryId={cat.id}
                  onEditSub={onEditSub} onDeleteSub={onDeleteSub}
                  onAddItem={onAddItem} onEditItem={onEditItem} onDeleteItem={onDeleteItem} />
              ))}
            </Box>
          )}

          {/* Direct items (items in category without subcategory) */}
          {directItems.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.8, pl: 1 }}>
                Direct Items
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                {directItems.map((item: any) => (
                  <ItemRow key={item.id} item={item} onEdit={onEditItem} onDelete={onDeleteItem} />
                ))}
              </Box>
            </Box>
          )}

          {subs.length === 0 && directItems.length === 0 && (
            <Typography sx={{ fontSize: 12, color: T.slateLt, py: 2, textAlign: "center", fontStyle: "italic" }}>
              Empty category — add subcategories or items
            </Typography>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN SERVICE TREE PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
const ServiceTreePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const [svc, setSvc] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Modal state
  const [catModal, setCatModal] = useState<{ open: boolean; category: any | null }>({ open: false, category: null });
  const [subModal, setSubModal] = useState<{ open: boolean; subcategory: any | null; categoryId: number; categoryName: string }>(
    { open: false, subcategory: null, categoryId: 0, categoryName: "" }
  );
  const [itemModal, setItemModal] = useState<{ open: boolean; item: any | null; categoryId: number; subcategoryId: number | null; parentLabel: string }>(
    { open: false, item: null, categoryId: 0, subcategoryId: null, parentLabel: "" }
  );
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string; type: string } | null>(null);

  const fetchService = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const res = await api.get(`/banquet/services-list/${id}`);
    if (res.code === 200) setSvc(res.data);
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchService(); }, [fetchService]);

  const categories = useMemo(() => svc?.categories ?? [], [svc]);

  const stats = useMemo(() => {
    let subCount = 0, itemCount = 0;
    categories.forEach((cat: any) => {
      subCount += (cat.subcategories ?? []).length;
      itemCount += (cat.items ?? []).length;
      (cat.subcategories ?? []).forEach((sub: any) => { itemCount += (sub.items ?? []).length; });
    });
    return { categories: categories.length, subcategories: subCount, items: itemCount };
  }, [categories]);

  // ── CATEGORY CRUD ──
  const handleSaveCategory = async (data: any) => {
    setSaving(true);
    const res = await api.post("/banquet/service-tree/category", data);
    if (res.code === 200) {
      showMessage(data.id ? "Category updated" : "Category created");
      setCatModal({ open: false, category: null });
      fetchService();
    } else showErrorMessage(res.message || "Failed");
    setSaving(false);
  };

  // ── SUBCATEGORY CRUD ──
  const handleSaveSubcategory = async (data: any) => {
    setSaving(true);
    const res = await api.post("/banquet/service-tree/subcategory", data);
    if (res.code === 200) {
      showMessage(data.id ? "Subcategory updated" : "Subcategory created");
      setSubModal({ open: false, subcategory: null, categoryId: 0, categoryName: "" });
      fetchService();
    } else showErrorMessage(res.message || "Failed");
    setSaving(false);
  };

  // ── ITEM CRUD ──
  const handleSaveItem = async (data: any) => {
    setSaving(true);
    const res = await api.post("/banquet/service-tree/item", data);
    if (res.code === 200) {
      showMessage(data.id ? "Item updated" : "Item created");
      setItemModal({ open: false, item: null, categoryId: 0, subcategoryId: null, parentLabel: "" });
      fetchService();
    } else showErrorMessage(res.message || "Failed");
    setSaving(false);
  };

  // ── DELETE ──
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const endpoint = deleteTarget.type === "category"
      ? `/banquet/service-tree/category/${deleteTarget.id}`
      : deleteTarget.type === "subcategory"
        ? `/banquet/service-tree/subcategory/${deleteTarget.id}`
        : `/banquet/service-tree/item/${deleteTarget.id}`;
    const res = await api.del(endpoint);
    if (res.code === 200) { showMessage(`${deleteTarget.type} deleted`); fetchService(); }
    else showErrorMessage(res.message);
    setDeleting(false);
    setDeleteTarget(null);
  };

  // ── Loading / 404 ──
  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress sx={{ color: T.gold }} /></Box>;
  }
  if (!svc) {
    return (
      <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 4 }, pb: 6 }}>
        <Typography sx={{ py: 10, textAlign: "center", color: T.slateLt }}>Service not found.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 4 }, pb: 6 }}>
      {/* ── Breadcrumb ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, pt: 1 }}>
        <IconButton size="small" onClick={() => navigate("/banquet/services-manager")} sx={{ color: T.slateLt }}>
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Typography sx={{ fontSize: 12, color: T.slateLt, cursor: "pointer", "&:hover": { color: T.gold } }}
          onClick={() => navigate("/banquet/services-manager")}>Services</Typography>
        <Typography sx={{ fontSize: 12, color: T.slateLt }}>/</Typography>
        <Typography sx={{ fontSize: 12, color: T.slateLt, cursor: "pointer", "&:hover": { color: T.gold } }}
          onClick={() => navigate(`/banquet/services-manager/${id}`)}>
          {svc.name}
        </Typography>
        <Typography sx={{ fontSize: 12, color: T.slateLt }}>/</Typography>
        <Typography sx={{ fontSize: 12, color: T.navy, fontWeight: 600 }}>Tree</Typography>
      </Box>

      {/* ── Header ── */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: "14px", border: `1px solid ${T.line}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: "14px", bgcolor: `${svc.color ?? T.slateLt}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <AccountTreeOutlinedIcon sx={{ fontSize: 26, color: svc.color ?? T.slateLt }} />
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: T.navy }}>{svc.name}</Typography>
              <Chip label={svc.service_status ?? svc.status ?? "active"} size="small" sx={{
                height: 22, fontSize: 10, fontWeight: 600,
                bgcolor: (svc.service_status ?? svc.status ?? "active") === "active" ? T.greenBg : T.redBg,
                color: (svc.service_status ?? svc.status ?? "active") === "active" ? T.greenTx : T.redTx,
              }} />
            </Box>
            {svc.description && <Typography sx={{ fontSize: 13, color: T.slateLt, mt: 0.3 }}>{svc.description}</Typography>}
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => setCatModal({ open: true, category: null })}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 2.5, boxShadow: "none", "&:hover": { bgcolor: "#1a3550" } }}>
          Add Category
        </Button>
      </Paper>

      {/* ── Stats ── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5, mb: 3 }}>
        {[
          { label: "Categories", value: stats.categories, icon: <CategoryOutlinedIcon sx={{ fontSize: 18, color: "#3b82f6" }} />, bg: "#3b82f612" },
          { label: "Subcategories", value: stats.subcategories, icon: <FolderOpenOutlinedIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />, bg: "#8b5cf612" },
          { label: "Items", value: stats.items, icon: <InventoryOutlinedIcon sx={{ fontSize: 18, color: T.greenTx }} />, bg: T.greenBg },
        ].map((stat) => (
          <Paper key={stat.label} elevation={0} sx={{ p: 2, borderRadius: "12px", border: `1px solid ${T.line}`, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: stat.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>{stat.icon}</Box>
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: T.navy, lineHeight: 1 }}>{stat.value}</Typography>
              <Typography sx={{ fontSize: 11, color: T.slateLt }}>{stat.label}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* ── Category Tree ── */}
      {categories.length === 0 ? (
        <Paper elevation={0} sx={{ p: 5, textAlign: "center", borderRadius: "14px", border: `1px dashed ${T.line}` }}>
          <AccountTreeOutlinedIcon sx={{ fontSize: 48, color: T.slateLt, mb: 1 }} />
          <Typography sx={{ fontSize: 14, color: T.slateLt, mb: 2 }}>No categories yet. Start building your service tree.</Typography>
          <Button variant="outlined" startIcon={<AddIcon />}
            onClick={() => setCatModal({ open: true, category: null })}
            sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, borderColor: T.navy, color: T.navy, borderRadius: "10px" }}>
            Add First Category
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {categories.map((cat: any) => (
            <CategoryCard key={cat.id} cat={cat} serviceId={Number(id)} serviceColor={svc.color ?? T.slateLt}
              onEditCat={(c) => setCatModal({ open: true, category: c })}
              onDeleteCat={(c) => setDeleteTarget({ id: c.id, name: c.name, type: "category" })}
              onAddSub={(catId, catName) => setSubModal({ open: true, subcategory: null, categoryId: catId, categoryName: catName })}
              onEditSub={(s) => setSubModal({ open: true, subcategory: s, categoryId: s.category_id, categoryName: categories.find((c: any) => c.id === s.category_id)?.name ?? "" })}
              onDeleteSub={(s) => setDeleteTarget({ id: s.id, name: s.name, type: "subcategory" })}
              onAddItem={(catId, subId, parentLabel) => setItemModal({ open: true, item: null, categoryId: catId, subcategoryId: subId, parentLabel })}
              onEditItem={(i) => setItemModal({ open: true, item: i, categoryId: i.category_id, subcategoryId: i.subcategory_id, parentLabel: "" })}
              onDeleteItem={(i) => setDeleteTarget({ id: i.id, name: i.name, type: "item" })}
            />
          ))}
        </Box>
      )}

      {/* ── Modals ── */}
      <CategoryModal open={catModal.open} category={catModal.category} serviceId={Number(id)}
        onSave={handleSaveCategory} onClose={() => setCatModal({ open: false, category: null })} saving={saving} />

      <SubcategoryModal open={subModal.open} subcategory={subModal.subcategory} serviceId={Number(id)}
        categoryId={subModal.categoryId} categoryName={subModal.categoryName}
        onSave={handleSaveSubcategory} onClose={() => setSubModal({ open: false, subcategory: null, categoryId: 0, categoryName: "" })} saving={saving} />

      <ItemModal open={itemModal.open} item={itemModal.item} serviceId={Number(id)}
        categoryId={itemModal.categoryId} subcategoryId={itemModal.subcategoryId} parentLabel={itemModal.parentLabel}
        onSave={handleSaveItem} onClose={() => setItemModal({ open: false, item: null, categoryId: 0, subcategoryId: null, parentLabel: "" })} saving={saving} />

      <DeleteDialog target={deleteTarget} deleting={deleting} onConfirm={handleDelete} onClose={() => setDeleteTarget(null)} />
    </Container>
  );
};

export default ServiceTreePage;
